import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import apiService from '../services/apiService';
import toast from 'react-hot-toast';
import {
  DollarSign,
  Filter,
  Download,
  TrendingUp,
  Activity,
  PieChart,
  LineChart,
  Package
} from 'lucide-react';

const SalesReport = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Report data state
  const [salesData, setSalesData] = useState({
    totalSales: 0,
    totalTransactions: 0,
    averageOrderValue: 0,
    salesComposition: {
      regularSales: { amount: 0, percentage: 0, transactions: 0, averageOrderValue: 0 },
      bulkSales: { amount: 0, percentage: 0, transactions: 0, averageOrderValue: 0 }
    },
    topProducts: [],
    salesByPaymentMethod: [],
    dailySales: [],
    monthlyTrend: []
  });

  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });


  // Helper function to format currency
  const formatCurrency = (amount) => {
    return `Ghs ${parseFloat(amount || 0).toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  // Fetch sales report data
  const fetchSalesReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getSalesReport(dateRange.startDate, dateRange.endDate);
      if (response.success) {
        setSalesData(response.data);
      } else {
        toast.error('Failed to fetch sales report data');
      }
    } catch (error) {
      console.error('Error fetching sales report:', error);
      toast.error('Failed to fetch sales report data');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // Load data on component mount and date change
  useEffect(() => {
    fetchSalesReport();
  }, [fetchSalesReport]);

  // Export report
  const handleExportReport = async (format) => {
    try {
      const response = await apiService.exportSalesReport(dateRange.startDate, dateRange.endDate, format);
      if (response.success) {
        // Create download link
        const blob = new Blob([response.data], { type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `sales-report-${dateRange.startDate}-to-${dateRange.endDate}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success(`Sales report exported successfully as ${format.toUpperCase()}!`, {
          duration: 3000,
          icon: '📊',
          style: {
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#fff',
          },
        });
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export sales report');
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
            {/* Page Header */}
            <div className="mb-6 sm:mb-8 lg:mb-10">
              <div className="flex flex-col gap-4 sm:gap-6">
                {/* Title and Date Row */}
                <div className="flex flex-col sm:flex-row mt-16 sm:items-center sm:justify-between gap-4">
                  <div className="space-y-2">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-indigo-900 bg-clip-text text-transparent">
                      Sales Report
                    </h1>
                    {/* <p className="text-gray-600 text-sm sm:text-base lg:text-lg">Comprehensive sales analytics and performance insights</p> */}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <div className="flex gap-2">
                      <input
                        type="date"
                        value={dateRange.startDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                      <input
                        type="date"
                        value={dateRange.endDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    </div>
                    <button
                      onClick={fetchSalesReport}
                      disabled={loading}
                      className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      {loading ? 'Loading...' : 'Apply Filter'}
                    </button>
                  </div>
                </div>
                
                {/* Export Buttons Row */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={() => handleExportReport('pdf')}
                    className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm font-medium"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export PDF
                  </button>
                  <button
                    onClick={() => handleExportReport('excel')}
                    className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm font-medium"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Excel
                  </button>
                </div>
              </div>
            </div>

            {/* Sales Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {/* Total Sales */}
              <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-blue-100/50 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Sales</p>
                    <p className="text-2xl sm:text-3xl font-bold text-blue-600">{formatCurrency(salesData.totalSales)}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>

              {/* Total Transactions */}
              <div className="bg-gradient-to-br from-white via-green-50/30 to-emerald-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-green-100/50 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Transactions</p>
                    <p className="text-2xl sm:text-3xl font-bold text-green-600">{salesData.totalTransactions}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                    <Activity className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>

              {/* Average Order Value */}
              <div className="bg-gradient-to-br from-white via-purple-50/30 to-violet-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-purple-100/50 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Avg Order Value</p>
                    <p className="text-2xl sm:text-3xl font-bold text-purple-600">{formatCurrency(salesData.averageOrderValue)}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-violet-600 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>

              {/* Growth Rate */}
              <div className="bg-gradient-to-br from-white via-orange-50/30 to-amber-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-orange-100/50 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Growth Rate</p>
                    <p className="text-2xl sm:text-3xl font-bold text-orange-600">+12.5%</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </div>

            {/* Sales Composition Breakdown */}
            <div className="bg-gradient-to-br from-white via-purple-50/30 to-indigo-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-purple-100/50 backdrop-blur-sm mb-6 sm:mb-8">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                  <Activity className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Sales Composition Breakdown</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Regular Sales */}
                <div className="bg-white/60 rounded-lg p-4 border border-blue-200/50">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-gray-700">Regular Sales</h4>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                      {salesData.salesComposition?.regularSales?.percentage || 0}%
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-blue-600 mb-1">
                    {formatCurrency(salesData.salesComposition?.regularSales?.amount || 0)}
                  </p>
                  <div className="text-xs text-gray-600 space-y-1">
                    <p>Transactions: {salesData.salesComposition?.regularSales?.transactions || 0}</p>
                    <p>Avg Order: {formatCurrency(salesData.salesComposition?.regularSales?.averageOrderValue || 0)}</p>
                  </div>
                </div>

                {/* Bulk Sales */}
                <div className="bg-white/60 rounded-lg p-4 border border-green-200/50">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-gray-700">Bulk Sales</h4>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                      {salesData.salesComposition?.bulkSales?.percentage || 0}%
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-green-600 mb-1">
                    {formatCurrency(salesData.salesComposition?.bulkSales?.amount || 0)}
                  </p>
                  <div className="text-xs text-gray-600 space-y-1">
                    <p>Transactions: {salesData.salesComposition?.bulkSales?.transactions || 0}</p>
                    <p>Avg Order: {formatCurrency(salesData.salesComposition?.bulkSales?.averageOrderValue || 0)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts and Analytics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
              {/* Sales by Payment Method */}
              <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-blue-100/50 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                    <PieChart className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Sales by Payment Method</h3>
                </div>
                <div className="space-y-3">
                  {salesData.salesByPaymentMethod.map((method, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-green-500' : index === 2 ? 'bg-purple-500' : 'bg-orange-500'}`}></div>
                        <span className="text-sm font-medium text-gray-700">{method.method}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">{formatCurrency(method.amount)}</p>
                        <p className="text-xs text-gray-500">{method.percentage}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Products */}
              <div className="bg-gradient-to-br from-white via-green-50/30 to-emerald-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-green-100/50 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mr-3">
                    <Package className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Top Selling Products</h3>
                </div>
                <div className="space-y-3">
                  {salesData.topProducts.map((product, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{product.name}</p>
                          <p className="text-xs text-gray-500">Qty: {product.quantity}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">{formatCurrency(product.revenue)}</p>
                        <p className="text-xs text-gray-500">{product.percentage}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Daily Sales Chart */}
            <div className="bg-gradient-to-br from-white via-purple-50/30 to-violet-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-purple-100/50 backdrop-blur-sm mb-6 sm:mb-8">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-violet-600 rounded-lg flex items-center justify-center mr-3">
                  <LineChart className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Daily Sales Trend</h3>
              </div>
              <div className="h-64 flex items-end justify-between space-x-2 p-4 bg-white/50 rounded-lg">
                {salesData.dailySales.map((day, index) => (
                  <div key={index} className="flex flex-col items-center space-y-2">
                    <div 
                      className="w-8 bg-gradient-to-t from-purple-500 to-violet-600 rounded-t-lg transition-all duration-300 hover:from-purple-600 hover:to-violet-700"
                      style={{ height: `${(day.amount / Math.max(...salesData.dailySales.map(d => d.amount))) * 200}px` }}
                    ></div>
                    <div className="text-xs text-gray-600 text-center">
                      <p className="font-medium">{day.date}</p>
                      <p className="text-gray-500">{formatCurrency(day.amount)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

      </div>
    </Layout>
  );
};

export default SalesReport;
